import React, { useState, useEffect, useMemo } from "react";
import { X, Layers, AlertCircle, CheckCircle, Search, Mail, Send, Loader2 } from "lucide-react";
import { useHR } from "../../context/HRContext";
import toast from "react-hot-toast";
import { MONTHS, MONTH_OPTIONS, getDepartmentLabel } from "../../constants/hrConstants";

export function SendPayslipModal({ isOpen, onClose, month, year, onGenerate }) {
  const { employees, paySetups, payslips, bulkGeneratePayslips } = useHR();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const [selectedMonth, setSelectedMonth] = useState(month || String(new Date().getMonth() + 1).padStart(2, "0"));
  const [selectedYear, setSelectedYear] = useState(year || String(new Date().getFullYear()));

  const yearOptions = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);

  // Compute status for each employee
  const employeeData = useMemo(() => {
    return employees
      .filter((e) => e.status === "active")
      .map((emp) => {
        const hasSetup = paySetups.some((s) => s.employeeId === emp.id);
        const hasPayslip = payslips.some((p) => p.employeeId === emp.id && p.month === String(selectedMonth).padStart(2, "0") && p.year === String(selectedYear));
        
        let status = "eligible";
        if (!hasSetup) status = "no_setup";
        else if (hasPayslip) status = "already_generated";

        return { ...emp, status };
      });
  }, [employees, paySetups, payslips, selectedMonth, selectedYear]);

  const filteredEmployees = useMemo(() => {
    return employeeData.filter(emp => 
      emp.name.toLowerCase().includes(search.toLowerCase()) || 
      emp.id.toLowerCase().includes(search.toLowerCase())
    );
  }, [employeeData, search]);

  const eligibleCount = filteredEmployees.filter(e => e.status === "eligible" || e.status === "already_generated").length;

  // Initialize selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedIds(new Set());
      setSearch("");
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const monthName = MONTHS[Number(month) - 1];

  const handleToggleSelect = (id, status) => {
    if (status === "no_setup") return;
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };

  const filteredEligible = filteredEmployees.filter(e => e.status === "eligible" || e.status === "already_generated");
  const isAllFilteredSelected = eligibleCount > 0 && filteredEligible.every(e => selectedIds.has(e.id));

  const handleToggleAll = () => {
    if (isAllFilteredSelected) {
      // Deselect only the currently filtered ones
      const next = new Set(selectedIds);
      filteredEligible.forEach(e => next.delete(e.id));
      setSelectedIds(next);
    } else {
      // Select all eligible from filtered list
      const next = new Set(selectedIds);
      filteredEligible.forEach(e => next.add(e.id));
      setSelectedIds(next);
    }
  };

  const handleGenerate = async () => {
    if (selectedIds.size === 0) {
      toast.error("Please select at least one employee.");
      return;
    }
    
    setIsGenerating(true);
    try {
      const res = await bulkGeneratePayslips({
        month: selectedMonth,
        year: selectedYear,
        employeeIds: Array.from(selectedIds)
      });
      if (res && res.status === "success") {
        toast.success(res.message || "Emails sent successfully!");
        onGenerate();
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to send emails");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-[16px] font-bold text-slate-800 flex items-center gap-2">
              <Layers className="w-5 h-5 text-blue-600" /> Send Emails
            </h2>
            <p className="text-[13px] text-slate-500 mt-0.5">
              Select employees to generate and email payslips
            </p>
          </div>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-3 bg-slate-50/50">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              className="input pl-9 text-[13px] bg-white"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="input w-36 text-[13px] bg-white" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            {MONTH_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
          <select className="input w-28 text-[13px] bg-white" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value)}>
            {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <div className="ml-auto text-[13px] text-slate-600 font-medium bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
            Selected: <span className="text-blue-600 font-bold">{selectedIds.size}</span> / {eligibleCount} eligible
          </div>
        </div>

        {/* Employee List */}
        <div className="flex-1 p-6 flex flex-col min-h-0">
          <div className="border border-slate-200 rounded-xl flex-1 overflow-auto bg-white">
            <table className="w-full text-left relative">
              <thead className="bg-slate-50 sticky top-0 z-10 shadow-[0_1px_0_0_#e2e8f0]">
                <tr>
                  <th className="px-4 py-3 w-12 text-center">
                    <input 
                      type="checkbox" 
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                      checked={isAllFilteredSelected}
                      onChange={handleToggleAll}
                      disabled={eligibleCount === 0}
                    />
                  </th>
                  <th className="px-4 py-3 text-[12px] font-semibold text-slate-600 uppercase tracking-wider">Employee</th>
                  <th className="px-4 py-3 text-[12px] font-semibold text-slate-600 uppercase tracking-wider">Department</th>
                  <th className="px-4 py-3 text-[12px] font-semibold text-slate-600 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="px-4 py-8 text-center text-slate-500 text-[13px]">
                      No active employees found matching "{search}"
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp) => {
                    const isEligible = emp.status === "eligible" || emp.status === "already_generated";
                    const isSelected = selectedIds.has(emp.id);
                    return (
                      <tr 
                        key={emp.id} 
                        className={`transition-colors ${isEligible ? "hover:bg-slate-50/80 cursor-pointer" : "bg-slate-50/40 opacity-75"}`}
                        onClick={() => handleToggleSelect(emp.id, emp.status)}
                      >
                        <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                          <input 
                            type="checkbox" 
                            className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                            checked={isSelected}
                            onChange={() => handleToggleSelect(emp.id, emp.status)}
                            disabled={!isEligible}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-800 text-[14px]">{emp.name}</div>
                          <div className="text-[12px] text-slate-500 font-mono mt-0.5">{emp.id}</div>
                        </td>
                        <td className="px-4 py-3 text-[13px] text-slate-600 capitalize">
                          {getDepartmentLabel(emp.department) || "N/A"}
                        </td>
                        <td className="px-4 py-3">
                          {emp.status === "eligible" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-blue-50 text-blue-600 border border-blue-100">
                              <CheckCircle className="w-3.5 h-3.5" /> Ready to generate payslip and send email
                            </span>
                          )}
                          {emp.status === "no_setup" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-red-50 text-red-600 border border-red-100">
                              <AlertCircle className="w-3.5 h-3.5" /> Pay setup not configured
                            </span>
                          )}
                          {emp.status === "already_generated" && (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium bg-green-50 text-green-600 border border-green-100">
                              <CheckCircle className="w-3.5 h-3.5" /> Already generated
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Generating Overlay */}
        {isGenerating && (
          <div className="absolute inset-0 z-20 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl animate-in fade-in duration-300">
            <div className="relative">
              <Mail className="w-16 h-16 text-blue-500 animate-bounce" />
              <div className="absolute -top-1 -right-1">
                <Send className="w-6 h-6 text-blue-600 animate-pulse translate-x-4 -translate-y-4" />
              </div>
            </div>
            <h3 className="mt-6 text-lg font-bold text-slate-800">Dispatching Emails...</h3>
            <p className="text-slate-500 text-[13px] mt-1">Please wait while we generate and send the payslips.</p>
            <Loader2 className="w-6 h-6 text-blue-500 animate-spin mt-4" />
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex justify-end gap-3 rounded-b-2xl relative z-10">
          <button onClick={onClose} disabled={isGenerating} className="btn-secondary">
            Cancel
          </button>
          <button 
            onClick={handleGenerate} 
            disabled={isGenerating || selectedIds.size === 0}
            className="btn-primary flex items-center gap-2 overflow-hidden relative"
          >
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> Sending...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" /> Send {selectedIds.size > 0 ? selectedIds.size : ""} Emails
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
}
